import React, { useCallback, useEffect, useState } from "react";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router-dom";
import { toast } from "sonner";
import { v4 as uuidv4 } from "uuid";

import AdBreakStructureBlock from "./AdBreakStructureBlock";
import FastAPI from "../../../api/FastAPI";
import MediaAPI from "../../../api/MediaAPI";
import { AD_BREAK_TYPES } from "../../../constants/fast";
import { MEDIA_FORMATS } from "../../../constants/media";
import useFASTLayout from "../../../hooks/useFASTLayout";
import { CONTENT_TYPE } from "../../content/Type";
import NameHeaderInput from "../../form/NameHeaderInput";
import SaveButton from "../../form/SaveButton";

import type { JSX } from "react";

import type {
  GetAdBreakResponse,
  UpdatedAdBreak,
} from "../../../api/FastAPI.types";
import type { AdBreakTypes } from "../../../constants/fast";
import type {
  AdBreakDropzoneMedia,
  AdBreakListByType,
} from "../../../types/fast";
import type { FASTChannelRoutesParams } from "../../routes/FASTChannelRoutes";

const { ADPODS, PRE_ADS_BUMPER, POST_ADS_BUMPER } = AD_BREAK_TYPES;

export default function AdBreakForm(): JSX.Element {
  const { channelId, adbreakId } = useParams<
    FASTChannelRoutesParams & {
      adbreakId: string;
    }
  >();
  const queryClient = useQueryClient();
  const { t } = useTranslation("editor-fast-channel");
  const history = useHistory();
  const isNewAdBreak = adbreakId === "new-adbreak";

  const [adbreakListByType, setAdbreakListByType] = useState<AdBreakListByType>(
    {
      [ADPODS]: {
        mediaList: [],
        duration: 0,
      },
      [PRE_ADS_BUMPER]: {
        mediaList: [],
        duration: 0,
      },
      [POST_ADS_BUMPER]: {
        mediaList: [],
        duration: 0,
      },
    },
  );

  const getMediasMetadataListQuery = MediaAPI.generateQuery({
    fn: MediaAPI.getMediasMetadataList,
    params: [],
  });

  const { data: metadataData } = useQuery(getMediasMetadataListQuery);

  const getAdbreakQuery = FastAPI.generateQuery({
    fn: FastAPI.getAdBreak,
    params: [channelId, adbreakId],
  });

  const { data: getAdbreak, isFetching: isGetAdBreakFetching } = useQuery({
    ...getAdbreakQuery,
    enabled: !isNewAdBreak,
  });

  const updateAdBreakMutation = useMutation({
    mutationFn: async (updatedAdbreak: UpdatedAdBreak) =>
      isNewAdBreak
        ? FastAPI.createAdBreak(channelId, updatedAdbreak)
        : FastAPI.updateAdBreak(channelId, adbreakId, updatedAdbreak),
    onMutate: (updatedAdbreak: UpdatedAdBreak) => {
      if (isNewAdBreak) return undefined;

      queryClient.cancelQueries(getAdbreakQuery.queryKey);

      const previousAdbreak = queryClient.getQueryData<GetAdBreakResponse>(
        getAdbreakQuery.queryKey,
      );

      if (!isNewAdBreak) {
        queryClient.setQueryData(getAdbreakQuery.queryKey, {
          ...previousAdbreak,
          data: {
            ...previousAdbreak?.data,
            ...updatedAdbreak,
          },
        });
      }

      return { previousAdbreak };
    },
    onSuccess: ({ data }) => {
      toast.success(
        isNewAdBreak ? t("adbreakCreationSuccess") : t("adbreakUpdateSuccess"),
      );

      if (isNewAdBreak) {
        history.replace(data.uuid);
      }
      const getAdBreaksListQuery = FastAPI.generateQuery({
        fn: FastAPI.getAdBreaksList,
        params: [channelId],
      });

      queryClient.invalidateQueries(getAdBreaksListQuery.queryKey);
    },
    onError: () => {
      toast.error(
        isNewAdBreak ? t("adbreakCreationError") : t("adbreakUpdateError"),
      );
    },
  });

  const bumperFormat = metadataData?.formats.find(
    (format) => format.key === MEDIA_FORMATS.BUMPER,
  );
  const adpodFormat = metadataData?.formats.find(
    (format) => format.key === MEDIA_FORMATS.ADPOD,
  );
  const promotionFormat = metadataData?.formats.find(
    (format) => format.key === MEDIA_FORMATS.PROMOTION,
  );

  const handleFormSubmit = (adBreakName: string): void => {
    const hasPreAdsBumper = adbreakListByType[PRE_ADS_BUMPER].mediaList.length;
    const hasPostAdsBumper =
      adbreakListByType[POST_ADS_BUMPER].mediaList.length;
    if (
      (hasPreAdsBumper && !hasPostAdsBumper) ||
      (hasPostAdsBumper && !hasPreAdsBumper)
    ) {
      toast.error(t("dropzoneBumpersRequired"));

      return;
    }
    if (!adbreakListByType.adpods.mediaList.length) {
      toast.error(t("dropzoneAdpodsRequired"));

      return;
    }

    const preAdsBumperList = adbreakListByType[PRE_ADS_BUMPER].mediaList;
    const adpodList = adbreakListByType[ADPODS].mediaList.map((media) => ({
      ...media,
      rank: media.rank + 1,
    }));
    const postAdsBumperList = adbreakListByType[POST_ADS_BUMPER].mediaList.map(
      (media) => ({
        ...media,
        rank: preAdsBumperList.length + adpodList.length,
      }),
    );

    const mediaList = [
      ...preAdsBumperList,
      ...adpodList,
      ...postAdsBumperList,
    ].map((media) => ({
      media_uuid: media.mediaId,
      duration: media.duration,
      format: media.formatKey,
      label: media.label,
      rank: media.rank,
    }));

    const updatedAdbreak = {
      name: adBreakName.trim(),
      medias: mediaList,
    };

    updateAdBreakMutation.mutate(updatedAdbreak);
  };

  const methods = useForm<{
    name: string;
    adpodDuration: number;
    adbreakDuration: number;
  }>({
    values: {
      name: isNewAdBreak ? t("newAdbreak") : (getAdbreak?.data?.name ?? ""),
      adpodDuration: 0,
      adbreakDuration: 0,
    },
  });

  const handleAdBreakChange = useCallback(
    (items: AdBreakDropzoneMedia[], type: AdBreakTypes): void => {
      const updatedTotalDuration = items.reduce(
        (acc, media) => acc + media.duration,
        0,
      );

      setAdbreakListByType((prevItems) => ({
        ...prevItems,
        [type]: {
          mediaList: items,
          duration: updatedTotalDuration,
        },
      }));
    },
    [],
  );

  useEffect(() => {
    // Set initial adbreak list and durations
    // Replace breadcrumb with adbreak name

    if (!getAdbreak?.data.medias.length) return;

    const adbreakDragItemList: AdBreakDropzoneMedia[] =
      getAdbreak.data.medias.map((media) => ({
        rank: media.rank,
        label: media.label,
        mediaId: media.media_uuid,
        duration: media.duration,
        formatKey: media.format,
        uuid: uuidv4(),
        type: CONTENT_TYPE.HOSTED,
      }));

    const adpodList = adbreakDragItemList
      .filter(
        (media) =>
          media.formatKey === MEDIA_FORMATS.ADPOD ||
          media.formatKey === MEDIA_FORMATS.PROMOTION,
      )
      .map((media) => ({
        ...media,
        rank: media.rank - 1,
      }));

    const bumperList = adbreakDragItemList.filter(
      (media) => media.formatKey === MEDIA_FORMATS.BUMPER,
    );

    const preAdsBumper = bumperList[0]
      ? [
          {
            ...bumperList[0],
            rank: 0,
          },
        ]
      : [];

    const postAdsBumper = bumperList[1]
      ? [
          {
            ...bumperList[1],
            rank: 0,
          },
        ]
      : [];

    const adpodDuration = adpodList.reduce(
      (acc, media) => acc + media.duration,
      0,
    );
    const preAdsBumperDuration = bumperList[0]?.duration ?? 0;
    const postAdsBumperDuration = bumperList[1]?.duration ?? 0;

    setAdbreakListByType({
      [ADPODS]: {
        mediaList: adpodList,
        duration: adpodDuration,
      },
      [PRE_ADS_BUMPER]: {
        mediaList: preAdsBumper,
        duration: preAdsBumperDuration,
      },
      [POST_ADS_BUMPER]: {
        mediaList: postAdsBumper,
        duration: postAdsBumperDuration,
      },
    });
  }, [getAdbreak?.data, history, methods]);

  const formId = "adbreak-edit-form";

  useFASTLayout({
    title: <NameHeaderInput control={methods.control} />,
    button: <SaveButton formId={formId} />,
    breadcrumbs: [
      {
        name: t("ad-breaks"),
        url: `/fast-channels/${channelId}/ad-breaks`,
      },
      {
        name: isNewAdBreak ? t("newAdbreak") : (getAdbreak?.data?.name ?? ""),
      },
    ],
  });

  return (
    // eslint-disable-next-line react/jsx-props-no-spreading
    <FormProvider {...methods}>
      <form
        className="flex size-full flex-col gap-[1.5625rem]"
        id={formId}
        onSubmit={methods.handleSubmit((data) => handleFormSubmit(data.name))}
      >
        <AdBreakStructureBlock
          adbreakListByType={adbreakListByType}
          formatsMetadata={{ bumperFormat, adpodFormat, promotionFormat }}
          handleAdBreakChange={handleAdBreakChange}
          isLoading={isGetAdBreakFetching}
        />
      </form>
    </FormProvider>
  );
}
